/* eslint-disable no-magic-numbers */

import {
  Button,
  DateFormats,
  DescriptionList,
  type DescriptionListItem,
  formatISODate,
  Inline,
  Skeleton,
  Stack,
  Table,
  Tag,
  type TagIntent,
  Typography,
  useModal,
} from '@omnipresentgroup/design-system';
import {
  isLiabilityShieldInactiveInContract,
  useContractor,
} from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  type LiabilityShield,
  LiabilityShieldState,
} from '@omnipresentgroup/types';
import {
  type ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { ContractorLiabilityShieldModal } from './ContractorLiabilityShieldModal';

const STATE_HEADER = 'Omniprotect';
const START_DATE_HEADER = 'Coverage start date';
const END_DATE_HEADER = 'Coverage end date';
const EMPTY_ARRAY: LiabilityShield[] = [];

const dateRenderer = (date?: string) =>
  date ? formatISODate(date, DateFormats.Date, true, '-') : '-';

const stateRenderer = (liabilityShieldState: LiabilityShieldState) => {
  const intentPerState: Record<LiabilityShieldState, TagIntent> = {
    [LiabilityShieldState.ACTIVE]: 'success',
    [LiabilityShieldState.INACTIVE]: 'default',
    [LiabilityShieldState.AWAITING_ACTIVATION]: 'info',
  };

  return (
    <Tag
      id={`liability-shield-state-${Math.floor(Math.random() * 100)}`}
      intent={intentPerState[liabilityShieldState]}
    >
      {toSentenceCase(liabilityShieldState)}
    </Tag>
  );
};

export const ContractorLiabilityShieldDetails = ({
  contractorId,
}: {
  contractorId: string;
}) => {
  const { data: contractor, isLoading } = useContractor({ id: contractorId });

  const { modalProps } = useModal({});
  const [modalOpen, setModalOpen] = useState(false);

  const liabilityShieldCoverages = useMemo(
    () => contractor?.contracts[0].liabilityShieldCoverages,
    [contractor?.contracts],
  );

  const showSingleLiabilityShield = useMemo(
    () => !liabilityShieldCoverages || liabilityShieldCoverages?.length <= 1,
    [liabilityShieldCoverages],
  );

  const showHeaderLearnMoreButton = useMemo(
    () =>
      !liabilityShieldCoverages ||
      liabilityShieldCoverages?.length === 0 ||
      (contractor?.contracts[0] &&
        isLiabilityShieldInactiveInContract(contractor.contracts[0])),
    [contractor?.contracts, liabilityShieldCoverages],
  );

  const singleLiabilityShieldDetails: DescriptionListItem[] = useMemo(() => {
    const singleLiabilityShield = liabilityShieldCoverages?.[0];
    return [
      {
        header: STATE_HEADER,
        detail: isLoading ? (
          <Skeleton />
        ) : (
          stateRenderer(
            singleLiabilityShield?.state || LiabilityShieldState.INACTIVE,
          )
        ),
      },
      ...(singleLiabilityShield?.startDate
        ? [
            {
              header: START_DATE_HEADER,
              detail: isLoading ? (
                <Skeleton />
              ) : (
                dateRenderer(singleLiabilityShield.startDate)
              ),
            },
            {
              header: END_DATE_HEADER,
              detail: isLoading ? (
                <Skeleton />
              ) : (
                dateRenderer(singleLiabilityShield.endDate)
              ),
            },
          ]
        : []),
    ];
  }, [isLoading, liabilityShieldCoverages]);

  const columnHelper = createColumnHelper<LiabilityShield>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<LiabilityShield, any>[] = useMemo(
    () => [
      columnHelper.accessor('startDate', {
        cell: (info) => dateRenderer(info.getValue()),
        header: () => START_DATE_HEADER,
      }),
      columnHelper.accessor('endDate', {
        cell: (info) => dateRenderer(info.getValue()),
        header: () => END_DATE_HEADER,
      }),
      columnHelper.accessor('state', {
        cell: (info) => stateRenderer(info.getValue()),
        header: () => STATE_HEADER,
      }),
    ],
    [columnHelper],
  );

  const table = useReactTable({
    // Core Config
    data: liabilityShieldCoverages || EMPTY_ARRAY,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Stack data-testid="liability-shield-details-list">
      <Inline justify="space-between" align="center">
        <Typography as="h3" hideParagraphSpacing size="20" weight="medium">
          Omniprotect
        </Typography>
        {showHeaderLearnMoreButton && (
          <Button
            variant="secondary"
            size="small"
            onClick={() => setModalOpen(true)}
          >
            Learn more
          </Button>
        )}
      </Inline>

      {showSingleLiabilityShield ? (
        <DescriptionList
          fraction="1/2"
          id="liability-shield-details-list"
          list={singleLiabilityShieldDetails}
        />
      ) : (
        <Table
          id="liability-shield-details-table"
          table={table}
          showHeader={false}
        />
      )}

      <ContractorLiabilityShieldModal
        modalProps={{
          ...modalProps,
          open: modalOpen,
          onOpenChange: setModalOpen,
        }}
        contractor={contractor}
        handleClose={() => setModalOpen(false)}
      />
    </Stack>
  );
};
